// src/pages/Contact.jsx
import { useState } from 'react';
import { supabase } from '../services/supabaseClient';

export default function Contact() {
  const [form,    setForm]    = useState({ name: '', email: '', phone: '', message: '' });
  const [sent,    setSent]    = useState(false);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState('');

  const handleChange = e => {
    setForm(f => ({ ...f, [e.target.name]: e.target.value }));
    setError('');
  };

  const handleSubmit = async e => {
    e.preventDefault();
    if (!form.name.trim())    { setError('Name is required.'); return; }
    if (!form.email.trim())   { setError('Email is required.'); return; }
    if (!/\S+@\S+\.\S+/.test(form.email)) { setError('Please enter a valid email address.'); return; }
    if (!form.message.trim()) { setError('Message is required.'); return; }

    setLoading(true); setError('');

    try {
      // 1. Save to Supabase contacts table
      const { error: dbErr } = await supabase.from('contacts').insert([{
        name:    form.name.trim(),
        email:   form.email.trim(),
        phone:   form.phone.trim(),
        message: form.message.trim(),
        status:  'Unread',
      }]);
      if (dbErr) throw dbErr;

      // 2. Send confirmation email to customer
      await supabase.functions.invoke('send-email', {
        body: {
          type: 'contact_confirmation',
          to:   form.email.trim(),
          data: { name: form.name.trim(), message: form.message.trim() },
        },
      });

      // 3. Send notification email to admin
      await supabase.functions.invoke('send-email', {
        body: {
          type: 'contact_admin',
          to:   'support@balconygreen.in',
          data: {
            name:    form.name.trim(),
            email:   form.email.trim(),
            phone:   form.phone.trim(),
            message: form.message.trim(),
          },
        },
      });

      setSent(true);
      setForm({ name: '', email: '', phone: '', message: '' });
    } catch (err) {
      console.error(err);
      setError('Something went wrong. Please try again or call us directly.');
    } finally {
      setLoading(false);
    }
  };

  const CONTACT_ITEMS = [
    {
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="#2e7d32" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5">
          <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.6 1.27h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 8.78a16 16 0 0 0 6 6l1.27-.9a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/>
        </svg>
      ),
      label: 'Phone', value: '+91-7302472992', href: 'tel:+917302472992', green: true,
    },
    {
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="#2e7d32" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5">
          <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/>
        </svg>
      ),
      label: 'Email', value: 'support@balconygreen.in', href: 'mailto:support@balconygreen.in', green: true,
    },
    {
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="#2e7d32" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5">
          <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/>
        </svg>
      ),
      label: 'Location', value: 'Ulwe, Navi Mumbai - 410206', href: null, green: false,
    },
    {
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="#2e7d32" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5">
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
        </svg>
      ),
      label: 'WhatsApp', value: 'Chat with us on WhatsApp', href: 'https://wa.me/917302472992', green: true,
    },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-green-50 border-b border-green-100 text-center py-2 text-sm text-gray-600 mt-16">
        Serving <span className="font-semibold text-gray-800">Ulwe, Navi Mumbai - 410206</span>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-12">
        <div className="text-center mb-10">
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-2">Get In Touch</h1>
          <p className="text-gray-500">Have questions? We'd love to hear from you!</p>
        </div>

        <div className="grid md:grid-cols-2 gap-8 items-start">
          {/* Left: Contact info */}
          <div>
            <h2 className="text-xl font-bold text-gray-900 mb-6">Contact Information</h2>
            <div className="space-y-5">
              {CONTACT_ITEMS.map(({ icon, label, value, href, green }) => (
                <div key={label} className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-green-50 rounded-xl flex items-center justify-center flex-shrink-0">
                    {icon}
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900 text-sm">{label}</p>
                    {href
                      ? <a href={href} className={`text-sm ${green ? 'text-[#2e7d32] hover:underline' : 'text-gray-600'}`}>{value}</a>
                      : <p className="text-sm text-gray-600">{value}</p>
                    }
                  </div>
                </div>
              ))}
            </div>

            {/* Service Hours */}
            <div className="mt-8 bg-green-50 border border-green-100 rounded-2xl p-5">
              <h3 className="font-bold text-gray-900 mb-3">Service Hours</h3>
              <div className="space-y-1.5 text-sm text-gray-600">
                <p>Monday - Sunday: 08:00 AM - 6:00 PM</p>
              </div>
            </div>
          </div>

          {/* Right: Contact form */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-5">Send us a Message</h2>

            {sent ? (
              <div className="text-center py-8">
                <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <svg viewBox="0 0 24 24" fill="none" stroke="#2e7d32" strokeWidth="2.5" className="w-7 h-7">
                    <polyline points="20 6 9 17 4 12"/>
                  </svg>
                </div>
                <p className="font-semibold text-gray-900 mb-1">Message Sent! 🎉</p>
                <p className="text-sm text-gray-500 mb-1">We'll get back to you within 24 hours.</p>
                <p className="text-xs text-gray-400 mb-4">A confirmation email has been sent to your email address.</p>
                <button onClick={() => setSent(false)}
                  className="px-5 py-2 border-2 border-[#2e7d32] text-[#2e7d32] text-sm font-semibold rounded-xl hover:bg-green-50 transition-colors">
                  Send Another
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} noValidate className="space-y-4">
                {error && (
                  <div className="px-4 py-3 bg-red-50 border border-red-200 text-red-700 rounded-xl text-sm">{error}</div>
                )}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Name *</label>
                  <input name="name" type="text" value={form.name} onChange={handleChange}
                    placeholder="Your name"
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#2e7d32]/30 focus:border-[#2e7d32]" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Email *</label>
                  <input name="email" type="email" value={form.email} onChange={handleChange}
                    placeholder="your@email.com"
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#2e7d32]/30 focus:border-[#2e7d32]" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Phone</label>
                  <input name="phone" type="tel" value={form.phone} onChange={handleChange}
                    placeholder="+91-XXXXXXXXXX"
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#2e7d32]/30 focus:border-[#2e7d32]" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Message *</label>
                  <textarea name="message" rows={4} value={form.message} onChange={handleChange}
                    placeholder="Your message..."
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#2e7d32]/30 focus:border-[#2e7d32] resize-none" />
                </div>
                <button type="submit" disabled={loading}
                  className="w-full py-3.5 bg-[#2e7d32] text-white font-semibold rounded-xl hover:bg-[#1b5e20] transition-colors duration-200 disabled:opacity-60">
                  {loading ? 'Sending...' : 'Send Message'}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
